import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import getName from "../util/getName";
import { AppContext } from "../util/AppContext";
import { ProductsModal, ProductsModalButton, isProductEnabledForCurrentTerritory } from "../components/ProductsModal";
import MessageTypes from "../util/messageTypes";
import { getConfigValue, isBatchPrintEnabledForProduct } from "../util/getConfigValue";
import AlertModal from '../components/AlertModal';
import { getBatchPrintMessages } from "../API/batchPrint";
import { useErrorBoundary } from "react-error-boundary";
import { focusTypeName, quickSheetTypeMessageId } from "../util/constants";
import { useRoutingValidation } from "../util/useRoutingValidation";
import { FavoriteDesignation, updateHcpAppState } from "../components/FavoriteDesignation";
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from "../components/CreateSalesAidStepProgressBar";
import { getPrescriberTypeLabel } from "../util/getHcpLabel";

function MessageSelectionPage() {
    const [appState, setAppState] = useContext(AppContext);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [productMessages, setProductMessages] = useState([]);
    const [currentProduct, setCurrentProduct] = useState();
    const [productImageSource, setProductImageSource] = useState();
    const { state } = useLocation();
    const [alert, setAlert] = useState(state?.alert || null);
    const prescriberType = (appState?.prescriberType);
    const focusType = (appState?.focus);
    const [batchPrintMessages, setBatchPrintMessages] = useState([]);
    const { showBoundary } = useErrorBoundary();
    const { setBreadcrumbs } = useBreadcrumbs();
    const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';
    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" },
        ]);
    }, [setBreadcrumbs]);

    useRoutingValidation();

    useEffect(() => {
		const getDefaultProduct = function () {
			for (let index = 0; index < appState?.sellSheetProductDetails.length; index++) {
				const clientProductId = appState?.sellSheetProductDetails[index].clientProductId;
				if (!isProductEnabledForCurrentTerritory(appState?.territory, clientProductId)) {
					continue;
				}
				const product = appState?.products?.find((p) => p.productId === clientProductId);
				if (product && product?.messages?.length > 0) {
					setCurrentProduct(appState?.sellSheetProductDetails[index]);
					setProductMessages(product.messages);
					setProductImageSource(appState?.sellSheetProductDetails[index].imagePath);
					setAppState({ ...appState, currentProduct: appState?.sellSheetProductDetails[index] });
					break;
				}
			}
		};
		if (
			appState?.currentProduct === '' ||
			!isProductEnabledForCurrentTerritory(appState?.territory, appState.currentProduct.clientProductId) ||
			!appState?.products?.find((p) => p.productId === appState.currentProduct?.clientProductId)
		) {
			getDefaultProduct();
		} else {
			setCurrentProduct(appState.currentProduct);
			setProductMessages(appState?.products?.find((i) => i.productId === appState.currentProduct?.clientProductId)?.messages);
			setProductImageSource(appState.currentProduct?.imagePath);
		}
	}, [appState, appState.currentProduct, appState?.sellSheetProductDetails, setAppState]);

    useEffect(() => {
        if ((focusType === focusTypeName.HCPs && (!productMessages || productMessages.length === 0)) || (focusType === focusTypeName.Messages && (!batchPrintMessages || batchPrintMessages.length === 0))) {
            setAlert({ type: 'primary', heading: (getConfigValue(appState?.config?.MessageSelectionPage?.InfoMessage?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.MessageSelectionPage?.InfoMessage?.Body, appState)?.Value || null) });
        } else {
            setAlert()
        }
    }, [appState, productMessages, batchPrintMessages])

    useEffect(() => {
        const getDefaultProductBatchPrint = async () => {
            for (let index = 0; index < appState?.sellSheetProductDetails.length; index++) {
                const product = appState?.sellSheetProductDetails[index].clientProductId;
                if (product && isBatchPrintEnabledForProduct(appState, product) && isProductEnabledForCurrentTerritory(appState?.territory, product)) {
                    setCurrentProduct(appState?.sellSheetProductDetails[index]);
                    setProductImageSource(appState?.sellSheetProductDetails[index].imagePath);
                    const result = await getBatchPrintMessages(appState?.territory.id, appState?.sellSheetProductDetails[index].clientProductId);
                    const categories = result.map(m => ({ category: m.name, messageCodes: m.messageCodes, messageTypeId: m.messageTypeId, messageType: m.messageType }));
                    setAppState({ ...appState, currentProduct: appState?.sellSheetProductDetails[index], batchPrintCategories: categories });
                    setBatchPrintMessages(categories);
                    break;
                }
            }
        }

        const getCurrentProductBatchPrint = async () => {
            if (isBatchPrintEnabledForProduct(appState, appState.currentProduct.clientProductId)) {
                setCurrentProduct(appState.currentProduct);
                setProductImageSource(appState.currentProduct?.imagePath);
                const result = await getBatchPrintMessages(appState?.territory.id, appState?.currentProduct.clientProductId);
                const categories = result.map(m => ({ category: m.name, messageCodes: m.messageCodes, messageTypeId: m.messageTypeId, messageType: m.messageType }));
                setAppState({ ...appState, batchPrintCategories: categories });
                setBatchPrintMessages(categories);
            } else {
                await getDefaultProductBatchPrint();
            }
        };
        if (focusType === focusTypeName.Messages) {
            if (appState?.currentProduct === "" || !isProductEnabledForCurrentTerritory(appState?.territory, appState.currentProduct.clientProductId)) {
                getDefaultProductBatchPrint().catch(error => { showBoundary(error) });
            } else {
                getCurrentProductBatchPrint().catch(error => { showBoundary(error) });
            }
        }
    }, [appState.territory.id, appState.currentProduct, appState?.sellSheetProductDetails, setAppState])

    function handleCategoryButtonClick(category) {
        setAppState({ ...appState, category: category, isQuickSheetMessageType: category.messageTypeId === quickSheetTypeMessageId});
    }

    function onProductClick(product) {
        setAppState({ ...appState, currentProduct: product });
        setCurrentProduct(product);
        setShowProductsModal(false);
        setProductMessages(appState?.products?.find(i => i.productId === product.clientProductId)?.messages)
        setProductImageSource(product.imagePath)
    }

    function handleNavigation(message) {
        const isContainerMessage = message.isContainerMessage;
        if (message.messageType === MessageTypes.ImprovedAccess) {
            return '/WinPlanListPage';
        }
        if (isContainerMessage) {
            return '/ContainerMessagePage';
        }
        if (message.messageTypeId === quickSheetTypeMessageId) {
            return '/QuickSheetsPage'
        }

        return '/PlanListPage';
    }

    return (
        <div>
            <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.CONFIGUREMESSAGE} focusType={focusType} />

            <div className="container-xl mt-3 mt-md-4" style={useStickyNavigation ? { marginBottom: '2rem' } : {}}>
                <div className="row d-flex align-items-md-center mb-md-5">
                    <div className="col-12 col-md-6 order-2 order-md-1">
                        <h1 className="type-20 color-grey-d1 mt-4 mt-md-0">
                            {getConfigValue(appState.config?.MessageSelectionPage?.CallToAction, appState)?.Value}
                        </h1>
                    </div>
                    <div className="col-12 col-md-6 order-1 order-md-2">
                        {focusType === focusTypeName.HCPs ?
                            <div className="row justify-content-end">
                                <div className="col-6 col-md-auto type-14 text-md-end favorite-hcp">
                                    <div className="color-grey-d1">
                                        {prescriberType && getPrescriberTypeLabel("MessageSelectionPage", prescriberType, appState)}
                                    </div>
                                    {appState.prescriber?.id && <FavoriteDesignation hcp={appState.prescriber} updateCallback={() => updateHcpAppState(appState.prescriber, setAppState)} ></FavoriteDesignation>}{getName(appState)}
                                </div>
                                <div className="col-6 col-md-auto type-14 text-md-end">
                                    <div className="color-grey-d1">
                                        {getConfigValue(appState.config?.MessageSelectionPage?.TerritoryLabel, appState)?.Value}
                                    </div>
                                    {appState.territory?.name}
                                </div>
                            </div>
                            : ""}
                    </div>
                </div>

                <div className="row">
                    {/* This section will be replaced with the config data to pull in the brands */}
                    <div className="col-12 col-md-4 col-lg-3">
                        <ProductsModalButton
                            setShowProductsModal={setShowProductsModal}
                            productImageSource={productImageSource}
                            productName={currentProduct?.name}
                        ></ProductsModalButton>
                    </div>
                    {alert && (
                        <div className="col-12 col-md-8 col-lg-9">
                            <AlertModal alert={alert} />
                        </div>
                    )}
                    {/* This section will be replaced with the config data to pull in the brands */}
                    <div className="col-12 col-md-8 col-lg-9">
                        {focusType === focusTypeName.HCPs && productMessages?.length > 0 &&
                            productMessages.toSorted((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder).map((message, index) => {
                                return (
                                    <React.Fragment key={message.messageCode}>
                                        {index > 0 && (
                                            <hr className="my-5" />
                                        )}

                                        <div className="row">
                                            <div className="col-12 col-lg-8">
                                                <h2 className="type-20 fw-bold">
                                                    {message.category}
                                                </h2>
                                                <div
                                                    className="my-md-0"
                                                    dangerouslySetInnerHTML={{
                                                        __html: message.message,
                                                    }}>
                                                </div>
                                            </div>
                                            <div className="col-12 col-lg-4 d-lg-flex flex-column justify-content-lg-center">
                                                {/* Link them to Improved access or Container Messages*/}
                                                <Link
                                                    to={handleNavigation(message)}
                                                    data-messagecode={message.messageCode}
                                                    onClick={() => handleCategoryButtonClick(message)}
                                                    className="d-inline-flex btn btn-outline-primary btn-icon mt-2"
                                                >
                                                    {message.category}{" "}
                                                    <span className="fa-regular fa-chevron-right btn-icon"></span>
                                                </Link>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        {focusType === focusTypeName.Messages && batchPrintMessages?.length > 0 &&
                            batchPrintMessages?.toSorted((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder).map((message, index) => {
                                return (
                                    <React.Fragment key={message.messageTypeId}>
                                        {index > 0 && (
                                            <hr className="my-5" />
                                        )}
                                        <div className="row">
                                            <div className="col-12 col-lg-8">
                                                <h2 className="type-20 fw-bold">
                                                    {message.category}
                                                </h2>
                                            </div>
                                            <div className="col-12 col-lg-4 d-lg-flex flex-column justify-content-lg-center">
                                                {/* Link them to Improved access */}
                                                <Link
                                                    to={'/SelectTargetBatchPage'}
                                                    onClick={() => handleCategoryButtonClick(message)}
                                                    className="d-inline-flex btn btn-outline-primary btn-icon mt-2"
                                                >
                                                    {message.category}{" "}
                                                    <span className="fa-regular fa-chevron-right btn-icon"></span>
                                                </Link>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })
                        }

                        <div className="my-7"></div>
                    </div>
                </div>
            </div>

            <div className={`${useStickyNavigation ? 'button-bottom-container-sticky-low-z' : ''}`}>
                <div className="container-xl">
                    <div className="row justify-content-center justify-content-md-end py-3">
                        <div className="col-12 col-md-8 col-lg-9">
                        </div>
                    </div>
                    <div className="row justify-content-center justify-content-md-end mb-5">
                        <div className="col-10 col-sm-8 col-md-4 col-lg-3">
                            {focusType === focusTypeName.HCPs &&
                                <Link
                                    to="/HCPPage"
                                    className="btn btn-secondary btn-flex flex-fill w-100">
                                    {getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
                                </Link>
                            }
                            {focusType === focusTypeName.Messages &&
                                <Link
                                    to={(appState?.bypassTerritorySelection) ? "/FocusPage" : "/TerritoryPage"}
                                    className="btn btn-secondary btn-flex flex-fill w-100">
                                    {getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
                                </Link>
                            }
                        </div>
                    </div>
                </div> 
            </div> 
            
            <ProductsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onProductClick={onProductClick}
                onlySellSheetProducts={true}
            />
        </div>
    );
}

export default MessageSelectionPage;
